import { useCallback, useEffect, useRef } from "react";
import { useDialog, useLoader } from "../context";
import axios from "axios";

/**
 * Formats errors into a user-friendly message.
 */
export const formatError = (err) => {
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.message) return err.message;
  return "No response received from server.";
};

/**
 * useDebouncedApiCall
 *
 * @param {number} delay - Debounce delay in ms (default: 300)
 * @param {Array<any>} deps - Dependency list that resets the debounce if changed
 *
 * @returns {Object} - { call, cancel }
 */
const useDebouncedApiCall = (delay = 10, deps = []) => {
  const { showLoader, hideLoader } = useLoader();
  const { showError } = useDialog();
  const debounceTimer = useRef(null);

  const cancel = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  }, []);

  const call = useCallback(
    (asyncFn, show = true) => {
      cancel();

      return new Promise((resolve, reject) => {
        debounceTimer.current = setTimeout(async () => {
          // Cancel previous request if any
          try {
            if (show) showLoader();
            const result = await asyncFn();
            resolve(result);
          } catch (err) {
            if (axios.isCancel(err)) {
            } else {
              // Check error.response before accessing nested properties
              showError(formatError(err));
            }
            reject(err);
          } finally {
            if (show) hideLoader();
          }
        }, delay);
      });
    },
    [delay, showLoader, hideLoader, showError, cancel]
  );

  // Cleanup on dependency change or unmount
  useEffect(() => {
    return cancel;
  }, [cancel, ...deps]);

  return { call, cancel };
};

export default useDebouncedApiCall;
